// src/services/reviewService.js - Complete Fixed Version

import {
  db,
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  query,
  orderBy,
  where,
  limit,
  startAfter,
  serverTimestamp,
} from './firebase';

// API endpoint for notifications
const NOTIFY_API_URL = '/api/notify';

// Helper to call notification API
async function sendNotification(notificationData) {
  try {
    const response = await fetch(NOTIFY_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(notificationData),
    });

    const result = await response.json();

    if (!result.success) {
      console.warn('Notification API returned error:', result.message);
      return { success: false, error: result.message };
    }

    console.log('✅ Notification sent successfully:', result);
    return result;
  } catch (error) {
    console.error('Failed to send notification:', error);
    return { success: false, error: error.message };
  }
}

// Validate email format
function isValidEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export const reviewService = {
  // ==================== SUBMIT REVIEW ====================
  async submitReview(reviewData, imageFile = null) {
    try {
      if (!reviewData.name) throw new Error('Name is required');
      if (!reviewData.rating) throw new Error('Rating is required');
      if (!reviewData.message) throw new Error('Message is required');
      if (reviewData.email && !isValidEmail(reviewData.email)) {
        throw new Error('Invalid email address');
      }

      const review = {
        name: reviewData.name.trim(),
        email: reviewData.email?.trim() || '',
        occupation: reviewData.occupation?.trim() || '',
        serviceType: reviewData.serviceType?.trim() || '',
        rating: parseInt(reviewData.rating),
        message: reviewData.message.trim(),
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        submittedAt: new Date().toISOString(),
      };

      let imageUrl = null;
      let imagePublicId = null;

      if (imageFile) {
        try {
          const cloudinaryCloudName = process.env.CLOUDINARY_CLOUD_NAME;
          const uploadPreset =
            process.env.CLOUDINARY_REVIEWS_UPLOAD_PRESET || 'reviews';

          if (cloudinaryCloudName) {
            const formData = new FormData();
            formData.append('file', imageFile);
            formData.append('upload_preset', uploadPreset);
            formData.append(
              'public_id',
              `reviews/${Date.now()}_${reviewData.name.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30)}`,
            );

            const uploadResponse = await fetch(
              `https://api.cloudinary.com/v1_1/${cloudinaryCloudName}/image/upload`,
              { method: 'POST', body: formData },
            );

            const uploadResult = await uploadResponse.json();
            if (uploadResult.secure_url) {
              imageUrl = uploadResult.secure_url;
              imagePublicId = uploadResult.public_id;
            }
          }
        } catch (uploadError) {
          console.error('Error uploading image:', uploadError);
        }
      }

      if (imageUrl) {
        review.imageUrl = imageUrl;
        review.imagePublicId = imagePublicId;
      }

      const docRef = await addDoc(collection(db, 'reviews'), review);
      const reviewId = docRef.id;

      const notificationResult = await sendNotification({
        type: 'review',
        reviewData: {
          id: reviewId,
          name: reviewData.name,
          email: reviewData.email || '',
          rating: parseInt(reviewData.rating),
          occupation: reviewData.occupation || '',
          serviceType: reviewData.serviceType || '',
          message: reviewData.message,
          imageUrl: imageUrl || null,
        },
        adminUserId: null,
        sendEmail: true,
        useAdminTemplate: true,
      });

      return {
        id: reviewId,
        ...review,
        notificationSent: notificationResult?.success || false,
      };
    } catch (error) {
      console.error('Error submitting review:', error);
      throw error;
    }
  },

  // ==================== GET ALL REVIEWS (Admin) - FIXED ====================
  async getAllReviews(filters = {}, lastDoc = null, limitCount = 20) {
    try {
      let q = query(
        collection(db, 'reviews'),
        orderBy('createdAt', 'desc'),
        limit(limitCount),
      );

      if (filters.status && filters.status !== 'all') {
        q = query(q, where('status', '==', filters.status));
      }

      if (filters.rating && filters.rating !== 'all') {
        q = query(q, where('rating', '==', parseInt(filters.rating)));
      }

      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const snapshot = await getDocs(q);
      const reviews = [];
      let lastVisible = null;

      snapshot.forEach(doc => {
        const data = doc.data();
        // FIX: Ensure createdAt is always a valid date string
        const createdAt = data.createdAt?.toDate?.()
          ? data.createdAt.toDate().toISOString()
          : data.createdAt || new Date().toISOString();

        reviews.push({
          id: doc.id,
          ...data,
          createdAt: createdAt,
          rating:
            typeof data.rating === 'number'
              ? data.rating
              : parseInt(data.rating) || 0,
        });
        lastVisible = doc;
      });

      return {
        reviews,
        lastVisible,
        hasMore: snapshot.docs.length === limitCount,
      };
    } catch (error) {
      console.error('Error getting reviews:', error);
      // FIX: Return empty array on error
      return {
        reviews: [],
        lastVisible: null,
        hasMore: false,
      };
    }
  },

  // ==================== GET APPROVED REVIEWS (Public) ====================
  async getApprovedReviews(limitCount = 20, lastDoc = null) {
    try {
      let q = query(
        collection(db, 'reviews'),
        where('status', '==', 'approved'),
        orderBy('createdAt', 'desc'),
        limit(limitCount),
      );

      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const snapshot = await getDocs(q);
      const reviews = [];
      let lastVisible = null;

      snapshot.forEach(doc => {
        const data = doc.data();
        reviews.push({
          id: doc.id,
          ...data,
          rating:
            typeof data.rating === 'number'
              ? data.rating
              : parseInt(data.rating) || 0,
        });
        lastVisible = doc;
      });

      return {
        reviews,
        lastVisible,
        hasMore: snapshot.docs.length === limitCount,
      };
    } catch (error) {
      console.error('Error getting approved reviews:', error);
      throw error;
    }
  },

  // ==================== GET SINGLE REVIEW ====================
  async getReview(id) {
    try {
      const docRef = doc(db, 'reviews', id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          rating:
            typeof data.rating === 'number'
              ? data.rating
              : parseInt(data.rating) || 0,
        };
      }
      return null;
    } catch (error) {
      console.error('Error getting review:', error);
      throw error;
    }
  },

  // ==================== UPDATE REVIEW ====================
  async updateReview(id, updateData) {
    try {
      const docRef = doc(db, 'reviews', id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Review not found');
      }

      const updates = {
        ...updateData,
        updatedAt: serverTimestamp(),
      };

      if (updateData.rating) {
        updates.rating = parseInt(updateData.rating);
      }

      await updateDoc(docRef, updates);
      return true;
    } catch (error) {
      console.error('Error updating review:', error);
      throw error;
    }
  },

  // ==================== UPDATE REVIEW STATUS ====================
  async updateReviewStatus(id, status, adminId = null) {
    try {
      const docRef = doc(db, 'reviews', id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Review not found');
      }

      const updates = {
        status,
        updatedAt: serverTimestamp(),
      };

      if (status === 'approved') {
        updates.approvedAt = serverTimestamp();
        updates.approvedBy = adminId;
      }

      if (status === 'rejected') {
        updates.rejectedAt = serverTimestamp();
        updates.rejectedBy = adminId;
      }

      await updateDoc(docRef, updates);
      return true;
    } catch (error) {
      console.error('Error updating review status:', error);
      throw error;
    }
  },

  // ==================== DELETE REVIEW ====================
  async deleteReview(id) {
    try {
      const docRef = doc(db, 'reviews', id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Review not found');
      }

      const reviewData = docSnap.data();

      if (reviewData.imagePublicId) {
        try {
          const cloudinaryCloudName = process.env.CLOUDINARY_CLOUD_NAME;
          const apiKey = process.env.CLOUDINARY_API_KEY;
          const apiSecret = process.env.CLOUDINARY_API_SECRET;

          if (cloudinaryCloudName && apiKey && apiSecret) {
            const authString = Buffer.from(`${apiKey}:${apiSecret}`).toString(
              'base64',
            );
            const formData = new URLSearchParams();
            formData.append('public_id', reviewData.imagePublicId);
            formData.append('invalidate', 'true');

            await fetch(
              `https://api.cloudinary.com/v1_1/${cloudinaryCloudName}/image/destroy`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded',
                  Authorization: `Basic ${authString}`,
                },
                body: formData.toString(),
              },
            );
          }
        } catch (error) {
          console.error('Error deleting image from Cloudinary:', error);
        }
      }

      await deleteDoc(docRef);
      return true;
    } catch (error) {
      console.error('Error deleting review:', error);
      throw error;
    }
  },

  // ==================== GET REVIEW STATS - FIXED ====================
  async getStats() {
    try {
      const snapshot = await getDocs(collection(db, 'reviews'));
      let total = 0;
      let approved = 0;
      let pending = 0;
      let rejected = 0;
      let totalRating = 0;

      // FIX: Initialize ratingDistribution with all keys
      const ratingDistribution = {
        5: 0,
        4: 0,
        3: 0,
        2: 0,
        1: 0,
      };

      snapshot.forEach(doc => {
        const data = doc.data();
        total++;

        if (data.status === 'approved') {
          approved++;
          // FIX: Ensure rating is a valid number
          const rating =
            typeof data.rating === 'number'
              ? data.rating
              : parseInt(data.rating) || 0;

          totalRating += rating;

          // Only count ratings 1-5
          const roundedRating = Math.round(rating);
          if (roundedRating >= 1 && roundedRating <= 5) {
            ratingDistribution[roundedRating] =
              (ratingDistribution[roundedRating] || 0) + 1;
          }
        } else if (data.status === 'pending') {
          pending++;
        } else if (data.status === 'rejected') {
          rejected++;
        }
      });

      const averageRating = approved > 0 ? totalRating / approved : 0;

      // FIX: Ensure all values are numbers
      const stats = {
        total: total || 0,
        approved: approved || 0,
        pending: pending || 0,
        rejected: rejected || 0,
        totalReviews: approved || 0,
        averageRating: parseFloat(averageRating.toFixed(1)) || 0,
        ratingDistribution: {
          5: ratingDistribution[5] || 0,
          4: ratingDistribution[4] || 0,
          3: ratingDistribution[3] || 0,
          2: ratingDistribution[2] || 0,
          1: ratingDistribution[1] || 0,
        },
      };

      console.log('📊 [reviewService] Stats:', stats);
      return stats;
    } catch (error) {
      console.error('Error getting review stats:', error);
      // FIX: Return default stats on error
      return {
        total: 0,
        approved: 0,
        pending: 0,
        rejected: 0,
        totalReviews: 0,
        averageRating: 0,
        ratingDistribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
      };
    }
  },

  // ==================== GET TIMELINE DATA ====================
  async getTimelineData(days = 30) {
    try {
      const snapshot = await getDocs(collection(db, 'reviews'));
      const timeline = {};
      const now = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      for (let i = days; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        const dateKey = date.toISOString().split('T')[0];
        timeline[dateKey] = { total: 0, approved: 0 };
      }

      snapshot.forEach(doc => {
        const data = doc.data();
        const createdAt = data.createdAt?.toDate();
        if (createdAt && createdAt >= startDate) {
          const dateKey = createdAt.toISOString().split('T')[0];
          if (timeline[dateKey]) {
            timeline[dateKey].total++;
            if (data.status === 'approved') {
              timeline[dateKey].approved++;
            }
          }
        }
      });

      return Object.entries(timeline).map(([date, counts]) => ({
        date,
        total: counts.total,
        approved: counts.approved,
        name: new Date(date).toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
        }),
      }));
    } catch (error) {
      console.error('Error getting timeline data:', error);
      return [];
    }
  },

  // ==================== GET RATING DISTRIBUTION DATA - FIXED ====================
  async getRatingDistributionData() {
    try {
      const stats = await this.getStats();
      const totalApproved = stats.approved || 1;

      // FIX: Ensure all ratings are present with proper counts
      const distribution = [
        {
          stars: 5,
          count: stats.ratingDistribution[5] || 0,
          percentage:
            ((stats.ratingDistribution[5] || 0) / totalApproved) * 100,
        },
        {
          stars: 4,
          count: stats.ratingDistribution[4] || 0,
          percentage:
            ((stats.ratingDistribution[4] || 0) / totalApproved) * 100,
        },
        {
          stars: 3,
          count: stats.ratingDistribution[3] || 0,
          percentage:
            ((stats.ratingDistribution[3] || 0) / totalApproved) * 100,
        },
        {
          stars: 2,
          count: stats.ratingDistribution[2] || 0,
          percentage:
            ((stats.ratingDistribution[2] || 0) / totalApproved) * 100,
        },
        {
          stars: 1,
          count: stats.ratingDistribution[1] || 0,
          percentage:
            ((stats.ratingDistribution[1] || 0) / totalApproved) * 100,
        },
      ];

      console.log('📊 [reviewService] Rating Distribution:', distribution);
      return distribution;
    } catch (error) {
      console.error('Error getting rating distribution:', error);
      return [
        { stars: 5, count: 0, percentage: 0 },
        { stars: 4, count: 0, percentage: 0 },
        { stars: 3, count: 0, percentage: 0 },
        { stars: 2, count: 0, percentage: 0 },
        { stars: 1, count: 0, percentage: 0 },
      ];
    }
  },

  // ==================== BULK ACTION ====================
  async bulkAction(ids, action) {
    try {
      const promises = ids.map(async id => {
        switch (action) {
          case 'delete':
            await this.deleteReview(id);
            break;
          case 'approve':
            await this.updateReviewStatus(id, 'approved');
            break;
          case 'reject':
            await this.updateReviewStatus(id, 'rejected');
            break;
          default:
            console.warn(`Unknown bulk action: ${action}`);
        }
      });

      await Promise.all(promises);
      return true;
    } catch (error) {
      console.error('Error performing bulk action:', error);
      throw error;
    }
  },
};

export default reviewService;
